const CONTROL_TOKEN_HEADER = 'X-Dispatcharr-Archive-Cast-Token';

const sleep = (milliseconds) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const finitePosition = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
};

class ArchiveCastStaleIntent extends Error {
  constructor() {
    super('A newer Archive Cast seek superseded this request');
    this.name = 'ArchiveCastStaleIntent';
  }
}

export class ArchiveCastController {
  constructor({
    fetchImpl = (...args) => fetch(...args),
    origin = window.location.origin,
    pollIntervalMs = 250,
    readyTimeoutMs = 30000,
    seekDebounceMs = 650,
    onSeekStart = () => {},
    onSeekComplete = () => {},
    onSeekError = () => {},
  } = {}) {
    this.fetchImpl = fetchImpl;
    this.origin = origin;
    this.pollIntervalMs = pollIntervalMs;
    this.readyTimeoutMs = readyTimeoutMs;
    this.seekDebounceMs = seekDebounceMs;
    this.onSeekStart = onSeekStart;
    this.onSeekComplete = onSeekComplete;
    this.onSeekError = onSeekError;

    this.castId = null;
    this.controlToken = null;
    this.current = null;
    this.pending = null;
    this.stopped = false;
    this.revision = 0;
    this.latestSeek = null;
    this.seekTimer = null;
    this.seekRunning = false;
    this.renewTimer = null;
  }

  get active() {
    return Boolean(this.castId && this.controlToken && !this.stopped);
  }

  get basePosition() {
    return finitePosition(this.current?.base_position);
  }

  get archiveDuration() {
    return finitePosition(this.current?.archive_duration);
  }

  _absoluteUrl(value) {
    return new URL(String(value || ''), this.origin);
  }

  _controlUrl(value, action) {
    const resolved = this._absoluteUrl(value);
    const expected = `/proxy/archive-cast-v2/${encodeURIComponent(this.castId)}/${action}`;
    if (resolved.origin !== this.origin || resolved.pathname !== expected) {
      throw new Error(`Invalid Archive Cast ${action} URL`);
    }
    return resolved.toString();
  }

  mediaUrl(payload = this.current) {
    const resolved = this._absoluteUrl(payload?.media_url);
    if (
      resolved.origin !== this.origin ||
      !resolved.pathname.startsWith('/archive-cast-media-v2/')
    ) {
      throw new Error('Invalid Archive Cast media URL');
    }
    // The capability-bearing media path is receiver-safe. The control token is
    // deliberately never copied to a URL, query string, CAF metadata or log.
    return resolved.toString();
  }

  _headers(withBody = false) {
    const headers = {};
    if (withBody) headers['Content-Type'] = 'application/x-www-form-urlencoded';
    if (this.controlToken) headers[CONTROL_TOKEN_HEADER] = this.controlToken;
    return headers;
  }

  async _responseJson(response, label) {
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(body?.error || `${label} failed (HTTP ${response.status})`);
      error.status = response.status;
      throw error;
    }
    return body;
  }

  async _post(url, fields, label, { keepalive = false } = {}) {
    const response = await this.fetchImpl(url, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      keepalive,
      headers: this._headers(true),
      body: new URLSearchParams(fields),
    });
    if (response.status === 204) return {};
    return this._responseJson(response, label);
  }

  _adopt(payload) {
    if (!payload?.cast_id || !payload?.control_token) {
      throw new Error('Archive Cast start returned an incomplete session');
    }
    this.castId = String(payload.cast_id);
    this.controlToken = String(payload.control_token);
    this.pending = payload;
    this.stopped = false;
    // Validate every server-provided endpoint before starting receiver work.
    this._controlUrl(payload.status_url, 'status');
    this._controlUrl(payload.activate_url, 'activate');
    this._controlUrl(payload.cancel_url, 'cancel');
    this._controlUrl(payload.seek_url, 'seek');
    this._controlUrl(payload.renew_url, 'renew');
    this._controlUrl(payload.stop_url, 'stop');
    this.mediaUrl(payload);
  }

  _isStale(revision) {
    return this.stopped || revision !== this.revision;
  }

  _assertCurrent(revision) {
    if (this._isStale(revision)) throw new ArchiveCastStaleIntent();
  }

  async _cancel(payload) {
    if (!payload?.generation || !this.controlToken || !this.castId) return;
    try {
      await this._post(
        this._controlUrl(payload.cancel_url, 'cancel'),
        { generation: payload.generation },
        'Archive Cast cancel'
      );
    } catch {
      // Cancellation is best effort. The worker also has a short lease and the
      // next backend seek replaces any still-pending generation.
    }
  }

  async _pollReady(payload, revision) {
    const deadline = Date.now() + this.readyTimeoutMs;
    let lastState = 'starting';
    while (Date.now() < deadline) {
      this._assertCurrent(revision);
      const statusUrl = this._absoluteUrl(
        this._controlUrl(payload.status_url, 'status')
      );
      statusUrl.searchParams.set('generation', payload.generation);
      const response = await this.fetchImpl(statusUrl.toString(), {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: this._headers(false),
      });
      const status = await this._responseJson(response, 'Archive Cast status');
      lastState = String(status?.state || 'starting');
      if (lastState === 'ready' || lastState === 'complete') {
        return { ...payload, ...status };
      }
      if (['error', 'expired', 'retired', 'stopped'].includes(lastState)) {
        throw new Error(
          status?.error || `Archive Cast generation stopped (${lastState})`
        );
      }
      await sleep(this.pollIntervalMs);
    }
    throw new Error(`Archive Cast generation readiness timed out (${lastState})`);
  }

  async _activate(payload, revision, { receiverAccepted = false } = {}) {
    if (!receiverAccepted) this._assertCurrent(revision);
    let attempts = 0;
    while (attempts < 5) {
      attempts += 1;
      try {
        await this._post(
          this._controlUrl(payload.activate_url, 'activate'),
          { generation: payload.generation },
          'Archive Cast activate'
        );
        break;
      } catch (error) {
        if (error?.status !== 423 || attempts >= 5) throw error;
        await sleep(100 * attempts);
      }
    }
    // Once CAF accepted this media, finish the transaction even if a newer
    // intent arrived during loadMedia(). Cancelling here would leave the
    // receiver pointing at a deleted pending spool. The newest intent remains
    // queued and runs immediately after this activation.
    if (!receiverAccepted) this._assertCurrent(revision);
    this.current = payload;
    this.pending = null;
    this._startRenewal(payload);
    return payload;
  }

  async _prepareAndActivate(payload, revision, loadGeneration) {
    let receiverAccepted = false;
    let activationAttempted = false;
    try {
      const ready = await this._pollReady(payload, revision);
      this._assertCurrent(revision);
      // CAF must accept and expose the new media before activate retires the old
      // generation. This is the transactional handoff boundary.
      await loadGeneration(ready, {
        mediaUrl: this.mediaUrl(ready),
        revision,
        markReceiverAccepted: () => {
          receiverAccepted = true;
        },
      });
      // A successful callback is itself proof that CAF accepted the media,
      // even when a minimal/test loader does not call the explicit marker.
      receiverAccepted = true;
      activationAttempted = true;
      return await this._activate(ready, revision, { receiverAccepted: true });
    } catch (error) {
      if (receiverAccepted) {
        if (!activationAttempted) {
          try {
            await this._activate(payload, revision, { receiverAccepted: true });
          } catch (activationError) {
            error.activationError = activationError;
          }
        }
      } else {
        await this._cancel(payload);
      }
      throw error;
    }
  }

  _startRenewal(payload) {
    if (this.renewTimer != null) window.clearInterval(this.renewTimer);
    const seconds = Math.max(10, Number(payload?.renew_seconds) || 30);
    this.renewTimer = window.setInterval(() => {
      void this.renew().catch(() => undefined);
    }, seconds * 1000);
  }

  async start({ channelId, sessionId, position = 0, loadGeneration }) {
    if (!channelId || !sessionId || typeof loadGeneration !== 'function') {
      throw new Error('Archive Cast start is missing channel/session/media loader');
    }
    if (this.active) {
      await this.stop();
    } else {
      this.dispose();
    }
    this.stopped = false;
    const revision = ++this.revision;
    const startUrl = new URL(
      `/proxy/archive-cast-v2/${encodeURIComponent(channelId)}/start`,
      this.origin
    ).toString();
    const response = await this.fetchImpl(startUrl, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        session_id: String(sessionId),
        position: String(finitePosition(position)),
      }),
    });
    const payload = await this._responseJson(response, 'Archive Cast start');
    this._assertCurrent(revision);
    try {
      this._adopt(payload);
      return await this._prepareAndActivate(payload, revision, loadGeneration);
    } catch (error) {
      if (!(error instanceof ArchiveCastStaleIntent)) {
        await this.stop().catch(() => undefined);
      }
      throw error;
    }
  }

  scheduleSeek(position, loadGeneration) {
    if (!this.active || typeof loadGeneration !== 'function') return null;
    const revision = ++this.revision;
    this.latestSeek = {
      revision,
      position: finitePosition(position),
      loadGeneration,
    };
    if (this.seekTimer != null) window.clearTimeout(this.seekTimer);
    this.seekTimer = window.setTimeout(() => {
      this.seekTimer = null;
      void this._drainSeeks();
    }, this.seekDebounceMs);
    return revision;
  }

  async _createSeek(job) {
    let attempts = 0;
    while (attempts < 5) {
      this._assertCurrent(job.revision);
      attempts += 1;
      try {
        return await this._post(
          this._controlUrl(this.current?.seek_url, 'seek'),
          { position: String(job.position) },
          'Archive Cast seek'
        );
      } catch (error) {
        if (error?.status !== 423 || attempts >= 5) throw error;
        await sleep(100 * attempts);
      }
    }
    throw new Error('Archive Cast seek remained busy');
  }

  async _drainSeeks() {
    if (this.seekRunning || !this.active) return;
    this.seekRunning = true;
    try {
      while (this.active && this.latestSeek) {
        // A newer intent owns a fresh trailing debounce window. If it made the
        // in-flight preparation stale, stop here; the timer will restart the
        // drain only after input has gone quiet.
        if (this.seekTimer != null) break;
        const job = this.latestSeek;
        this.latestSeek = null;
        let payload = null;
        try {
          this.onSeekStart(job.position);
          payload = await this._createSeek(job);
          this._assertCurrent(job.revision);
          this.pending = payload;
          const active = await this._prepareAndActivate(
            payload,
            job.revision,
            job.loadGeneration
          );
          this.onSeekComplete(active);
        } catch (error) {
          if (!(error instanceof ArchiveCastStaleIntent)) {
            this.onSeekError(error, job.position);
          }
        }
      }
    } finally {
      this.seekRunning = false;
      if (this.active && this.latestSeek && this.seekTimer == null) {
        this.seekTimer = window.setTimeout(() => {
          this.seekTimer = null;
          void this._drainSeeks();
        }, 0);
      }
    }
  }

  async renew() {
    if (!this.active || !this.current?.renew_url) return false;
    await this._post(
      this._controlUrl(this.current.renew_url, 'renew'),
      {},
      'Archive Cast renew'
    );
    return true;
  }

  async stop({ keepalive = false } = {}) {
    if (this.seekTimer != null) window.clearTimeout(this.seekTimer);
    if (this.renewTimer != null) window.clearInterval(this.renewTimer);
    this.seekTimer = null;
    this.renewTimer = null;
    this.latestSeek = null;
    this.stopped = true;
    this.revision += 1;
    const payload = this.current || this.pending;
    if (payload?.stop_url && this.castId && this.controlToken) {
      try {
        await this._post(
          this._controlUrl(payload.stop_url, 'stop'),
          {},
          'Archive Cast stop',
          { keepalive }
        );
      } finally {
        this.current = null;
        this.pending = null;
      }
    }
    return true;
  }

  dispose() {
    if (this.seekTimer != null) window.clearTimeout(this.seekTimer);
    if (this.renewTimer != null) window.clearInterval(this.renewTimer);
    this.seekTimer = null;
    this.renewTimer = null;
    this.latestSeek = null;
    this.stopped = true;
    this.revision += 1;
  }
}

export const archiveChannelIdFromDirectUrl = (value, origin = window.location.origin) => {
  try {
    const parsed = new URL(value, origin);
    const match = parsed.pathname.match(
      /^\/proxy\/catchup-direct\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
    );
    return match?.[1] || null;
  } catch {
    return null;
  }
};
